/* ============================================================================
   Ancona Pass 12 — client-side interaction layer (ancona-innovation.js)
   Five independently-guarded, additive features. No network writes, no PII,
   no cookies, no third-party code. Storage is versioned, capped, try/caught,
   and corrupt-JSON self-healing. Feature flags: the loading <script> tag's
   data-ancona-features attribute (comma list) — remove a token to disable a
   feature; remove the script tag (layout/theme.liquid) to disable everything.
   Features: saved | shortlist | return | explore | toc
   ============================================================================ */
(function () {
  'use strict';
  if (window.__anconaInnovation) return;
  window.__anconaInnovation = true;

  var script = document.currentScript;
  var featAttr = script ? script.getAttribute('data-ancona-features') : null;
  /* Absent attribute → all features; explicit empty string → none (documented revert path). */
  var FEATURES = (featAttr == null ? 'saved,shortlist,return,explore,toc' : featAttr).split(',').map(function (s) { return s.trim(); }).filter(Boolean);
  function on(f) { return FEATURES.indexOf(f) !== -1; }

  function track(ev, payload) {
    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') window.dataLayer.push(Object.assign({ event: 'ancona:' + ev }, payload || {}));
      document.dispatchEvent(new CustomEvent('ancona:' + ev, { detail: payload || {} }));
    } catch (e) { /* analytics must never break shopping */ }
  }

  var LS = null, SS = null;
  try { LS = window.localStorage; LS.setItem('ancona:v1:t', '1'); LS.removeItem('ancona:v1:t'); } catch (e) { LS = null; }
  try { SS = window.sessionStorage; SS.setItem('ancona:v1:t', '1'); SS.removeItem('ancona:v1:t'); } catch (e) { SS = null; }

  function sGet(store, key) {
    if (!store) return null;
    try {
      var v = store.getItem(key);
      if (!v) return null;
      var parsed = JSON.parse(v);
      return parsed;
    } catch (e) {
      try { store.removeItem(key); } catch (_) {}
      return null;
    }
  }
  function sSet(store, key, val) {
    if (!store) return false;
    try { store.setItem(key, JSON.stringify(val)); return true; } catch (e) { return false; }
  }

  /* Shared polite live region for state announcements. When a modal dialog is
     open, announcements must live INSIDE it or a screen reader in the modal
     context will not read them — activeDialogBody tracks the current dialog. */
  var liveRegion = null;
  var activeDialogBody = null;
  function makeLive(parent) {
    var lr = document.createElement('div');
    lr.className = 'ancona-visually-hidden';
    lr.setAttribute('aria-live', 'polite');
    lr.setAttribute('role', 'status');
    parent.appendChild(lr);
    return lr;
  }
  function announce(msg) {
    var target;
    if (activeDialogBody) {
      target = activeDialogBody.__live || (activeDialogBody.__live = makeLive(activeDialogBody));
    } else {
      target = liveRegion || (liveRegion = makeLive(document.body));
    }
    target.textContent = '';
    window.setTimeout(function () { target.textContent = msg; }, 30);
  }

  /* Minimal accessible dialog: focus in on open, Tab loop, Escape closes,
     focus restored to opener, body scroll restored. */
  function openDialog(titleText, buildBody, opener) {
    var overlay = document.createElement('div');
    overlay.className = 'ancona-dlg__overlay';
    var dlg = document.createElement('div');
    dlg.className = 'ancona-dlg';
    dlg.setAttribute('role', 'dialog');
    dlg.setAttribute('aria-modal', 'true');
    dlg.setAttribute('aria-label', titleText);
    var head = document.createElement('div');
    head.className = 'ancona-dlg__head';
    var title = document.createElement('h2');
    title.className = 'ancona-dlg__title';
    title.textContent = titleText;
    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'ancona-dlg__close';
    close.setAttribute('aria-label', 'Close');
    close.innerHTML = '&times;';
    head.appendChild(title); head.appendChild(close);
    var body = document.createElement('div');
    body.className = 'ancona-dlg__body';
    dlg.appendChild(head); dlg.appendChild(body);
    overlay.appendChild(dlg);

    var prevOverflow = document.body.style.overflow;
    var prevDialogBody = activeDialogBody;
    function destroy() {
      document.removeEventListener('keydown', onKey, true);
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      document.body.style.overflow = prevOverflow;
      activeDialogBody = prevDialogBody;
      if (opener && opener.focus) opener.focus();
    }
    function onKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); destroy(); return; }
      if (e.key === 'Tab') {
        var f = dlg.querySelectorAll('a[href], button:not([disabled]), [tabindex="0"]');
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    overlay.addEventListener('click', function (e) { if (e.target === overlay) destroy(); });
    close.addEventListener('click', destroy);
    document.addEventListener('keydown', onKey, true);
    activeDialogBody = body;
    buildBody(body, destroy);
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';
    close.focus();
    return { close: destroy, root: dlg, body: body };
  }

  /* ---- 1) SAVED PIECES (localStorage wishlist, cap 24) --------------------- */
  var SAVED_KEY = 'ancona:v1:saved';
  function savedList() {
    var v = sGet(LS, SAVED_KEY);
    return Array.isArray(v) ? v.filter(function (x) { return x && typeof x.h === 'string' && typeof x.u === 'string'; }) : [];
  }
  function savedWrite(list) { return sSet(LS, SAVED_KEY, list.slice(0, 24)); }
  function savedHas(handle) { return savedList().some(function (x) { return x.h === handle; }); }

  function initSaved() {
    var wrap = document.querySelector('.ancona-save');
    var btn = document.getElementById('AnconaSaveBtn');
    var trigger = document.getElementById('AnconaSavedOpen');
    /* The snippet ships the whole control hidden; reveal it ONLY when storage
       works and the feature is on, so a disabled feature / denied storage / JS
       failure never leaves a dead button on the PDP (progressive enhancement). */
    if (!btn || !LS) return;
    if (wrap) wrap.hidden = false;

    function refreshTrigger() {
      if (!trigger) return;
      var n = savedList().length;
      trigger.textContent = 'Your saved pieces (' + n + ')';
      trigger.hidden = n === 0;
    }

    {
      var d = btn.dataset;
      function reflect() {
        var saved = savedHas(d.handle);
        btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
        btn.querySelector('.ancona-save__label').textContent = saved ? 'Saved' : 'Save this piece';
        btn.classList.toggle('is-saved', saved);
      }
      btn.addEventListener('click', function () {
        var list = savedList();
        if (savedHas(d.handle)) {
          list = list.filter(function (x) { return x.h !== d.handle; });
          savedWrite(list);
          announce('Removed from saved pieces.');
          track('saved_remove', { handle: d.handle });
        } else {
          list.unshift({ h: d.handle, u: d.url, t: (d.title || '').slice(0, 80), p: (d.price || '').slice(0, 24), i: d.image || '', ts: Date.now() });
          if (!savedWrite(list)) { announce('Saving is unavailable in this browser.'); return; }
          announce('Saved in this browser.');
          track('saved_add', { handle: d.handle });
        }
        reflect(); refreshTrigger();
      });
      reflect();
    }

    if (trigger) {
      refreshTrigger();
      trigger.addEventListener('click', function () { openSavedDialog(trigger, refreshTrigger); });
    }
  }

  function itemRow(item, onRemove) {
    var li = document.createElement('li');
    li.className = 'ancona-dlg__item';
    var a = document.createElement('a');
    a.href = item.u;
    if (item.i) {
      var img = document.createElement('img');
      img.src = item.i; img.alt = ''; img.loading = 'lazy'; img.width = 64; img.height = 64;
      a.appendChild(img);
    }
    var meta = document.createElement('span');
    meta.className = 'ancona-dlg__meta';
    var t = document.createElement('span'); t.className = 'ancona-dlg__t'; t.textContent = item.t || item.h;
    meta.appendChild(t);
    if (item.p) { var p = document.createElement('span'); p.className = 'ancona-dlg__p'; p.textContent = item.p; meta.appendChild(p); }
    a.appendChild(meta);
    li.appendChild(a);
    if (onRemove) {
      var rm = document.createElement('button');
      rm.type = 'button'; rm.className = 'ancona-dlg__rm';
      rm.setAttribute('aria-label', 'Remove ' + (item.t || item.h) + ' from saved pieces');
      rm.textContent = 'Remove';
      rm.addEventListener('click', function () { onRemove(item, li); });
      li.appendChild(rm);
    }
    return li;
  }

  function openSavedDialog(opener, refreshTrigger) {
    openDialog('Your saved pieces', function (body, destroy) {
      var note = document.createElement('p');
      note.className = 'ancona-dlg__note';
      note.textContent = 'Saved in this browser.';
      body.appendChild(note);
      /* Empty-state message is a sibling of the list, never a child of role=list. */
      var emptyNote = document.createElement('p');
      emptyNote.className = 'ancona-dlg__note';
      emptyNote.textContent = 'Nothing saved yet.';
      emptyNote.hidden = true;
      var ul = document.createElement('ul');
      ul.className = 'ancona-dlg__list';
      ul.setAttribute('role', 'list');
      body.appendChild(emptyNote);
      body.appendChild(ul);

      function paint(focusHint) {
        ul.textContent = '';
        var list = savedList();
        emptyNote.hidden = list.length > 0;
        list.forEach(function (item) {
          ul.appendChild(itemRow(item, function (it, li) {
            savedWrite(savedList().filter(function (x) { return x.h !== it.h; }));
            /* Move focus BEFORE removing the row so it never falls to <body> and
               escapes the modal (keyboard/SR focus management). */
            var sibling = li.nextElementSibling || li.previousElementSibling;
            var nextFocus = (sibling && sibling.querySelector('.ancona-dlg__rm')) || body.querySelector('.ancona-dlg__clear') || body.querySelector('.ancona-dlg__share');
            li.remove();
            if (nextFocus && nextFocus.focus) nextFocus.focus();
            announce('Removed from saved pieces.');
            track('saved_remove', { handle: it.h });
            if (refreshTrigger) refreshTrigger();
            var saveBtn = document.getElementById('AnconaSaveBtn');
            if (saveBtn && saveBtn.dataset.handle === it.h) {
              saveBtn.setAttribute('aria-pressed', 'false');
              saveBtn.classList.remove('is-saved');
              saveBtn.querySelector('.ancona-save__label').textContent = 'Save this piece';
            }
            if (!savedList().length) emptyNote.hidden = false;
          }));
        });
      }
      paint();

      var actions = document.createElement('div');
      actions.className = 'ancona-dlg__actions';
      if (on('shortlist')) {
        var share = document.createElement('button');
        share.type = 'button'; share.className = 'ancona-btn ancona-dlg__share';
        share.textContent = 'Share shortlist';
        share.addEventListener('click', function () {
          var handles = savedList().slice(0, 8).map(function (x) { return x.h; });
          if (!handles.length) { announce('Nothing saved yet.'); return; }
          var url = location.origin + '/?shortlist=' + handles.map(encodeURIComponent).join(',');
          track('shortlist_share', { count: handles.length });
          if (navigator.share) {
            navigator.share({ title: 'Ancona shortlist', url: url })['catch'](function () {});
          } else if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(url).then(function () {
              announce('Link copied'); share.textContent = 'Link copied';
              window.setTimeout(function () { share.textContent = 'Share shortlist'; }, 2000);
            })['catch'](function () { window.prompt('Copy this shortlist link:', url); });
          } else {
            window.prompt('Copy this shortlist link:', url);
          }
        });
        actions.appendChild(share);
      }
      var clear = document.createElement('button');
      clear.type = 'button'; clear.className = 'ancona-dlg__clear';
      clear.textContent = 'Clear saved pieces';
      clear.addEventListener('click', function () {
        if (!window.confirm('Clear all saved pieces in this browser?')) return;
        savedWrite([]); paint(); announce('Saved pieces cleared.');
        if (refreshTrigger) refreshTrigger();
        var saveBtn = document.getElementById('AnconaSaveBtn');
        if (saveBtn) {
          saveBtn.setAttribute('aria-pressed', 'false');
          saveBtn.classList.remove('is-saved');
          saveBtn.querySelector('.ancona-save__label').textContent = 'Save this piece';
        }
      });
      actions.appendChild(clear);
      body.appendChild(actions);
    }, opener);
  }

  /* ---- 2) SHARED SHORTLIST VIEWER (?shortlist=h1,h2 — max 8, validated) --- */
  function initShortlistView() {
    var m = /[?&]shortlist=([^&#]+)/.exec(location.search);
    if (!m) return;
    var raw;
    try { raw = decodeURIComponent(m[1]); } catch (e) { return; } /* malformed %-encoding must not break other features */
    var handles = raw.split(',').filter(function (h) { return /^[a-z0-9][a-z0-9-]{0,120}$/.test(h); }).slice(0, 8);
    if (!handles.length) return;
    Promise.all(handles.map(function (h) {
      return fetch('/products/' + h + '.js').then(function (r) { return r.ok ? r.json() : null; })['catch'](function () { return null; });
    })).then(function (products) {
      var ok = products.filter(Boolean);
      if (!ok.length) return;
      openDialog('Shared shortlist', function (body) {
        var note = document.createElement('p');
        note.className = 'ancona-dlg__note';
        note.textContent = 'Pieces shared with you.';
        body.appendChild(note);
        var ul = document.createElement('ul');
        ul.className = 'ancona-dlg__list';
        ul.setAttribute('role', 'list');
        ok.forEach(function (p) {
          var img = (p.featured_image || '');
          if (img && img.indexOf('//') === 0) img = 'https:' + img;
          if (img) img += (img.indexOf('?') === -1 ? '?width=128' : '&width=128');
          ul.appendChild(itemRow({ h: p.handle, u: '/products/' + p.handle, t: p.title, p: '', i: img }, null));
        });
        body.appendChild(ul);
      }, document.body);
    });
  }

  /* ---- 3) BACK-TO-RESULTS CONTINUITY (sessionStorage, 60 min) -------------- */
  var RETURN_KEY = 'ancona:v1:return-context';
  var ARM_KEY = 'ancona:v1:return-armed';
  function initReturn() {
    if (!SS) return;
    var grid = document.getElementById('ProductGridContainer');
    var isResults = !!grid && (location.pathname.indexOf('/collections/') === 0 || location.pathname.indexOf('/search') === 0);

    if (isResults) {
      /* Remember where the shopper left the results — ignore modified/aux clicks
         (new tab, etc.) so the context reflects an actual same-tab departure. */
      grid.addEventListener('click', function (e) {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        var a = e.target && e.target.closest ? e.target.closest('a[href*="/products/"]') : null;
        if (!a) return;
        var hm = /\/products\/([a-z0-9-]+)/.exec(a.getAttribute('href') || '');
        sSet(SS, RETURN_KEY, {
          o: location.href,
          k: location.pathname.indexOf('/search') === 0 ? 'search' : 'collection',
          y: window.scrollY || 0,
          h: hm ? hm[1] : '',
          ts: Date.now()
        });
      });
      /* Restore scroll ONLY when the shopper used our explicit back link (armed
         flag), never on an ordinary navigation to the same URL. */
      var ctx = sGet(SS, RETURN_KEY);
      var armedFor = null;
      try { armedFor = SS.getItem(ARM_KEY); SS.removeItem(ARM_KEY); } catch (e) {}
      if (ctx && ctx.o === location.href && ctx.y > 0 && armedFor === location.href && Date.now() - ctx.ts < 3600000) {
        window.requestAnimationFrame(function () {
          window.setTimeout(function () { window.scrollTo(0, ctx.y); }, 60);
        });
      }
      return;
    }

    /* On PDP: offer the way back while the context is fresh. Appended at the END
       of the info column (below the buy box) so it never shifts above-the-fold
       content — no CLS on the highest-traffic template. */
    if (location.pathname.indexOf('/products/') === -1) return;
    var ctx2 = sGet(SS, RETURN_KEY);
    if (!ctx2 || !ctx2.o || Date.now() - ctx2.ts >= 3600000) return;
    var info = document.querySelector('.product__info-container') || document.querySelector('.product__info-wrapper');
    if (!info || document.querySelector('.ancona-back')) return;
    var wrap = document.createElement('p');
    wrap.className = 'ancona-back';
    var a = document.createElement('a');
    a.href = ctx2.o;
    a.textContent = ctx2.k === 'search' ? '← Back to search results' : '← Back to results';
    a.addEventListener('click', function () {
      try { SS.setItem(ARM_KEY, ctx2.o); } catch (e) {}
      track('return_to_results', { to: ctx2.k });
    });
    wrap.appendChild(a);
    info.appendChild(wrap);
  }

  /* ---- 4) CONTINUE EXPLORING (homepage; reuses recently-viewed data) ------ */
  function initExplore() {
    if (!LS) return;
    if (!document.querySelector('.ancona-hero2')) return; /* homepage only (Ancona hero present) */
    var raw = sGet(LS, 'ancona:recently-viewed');
    if (!Array.isArray(raw)) return;
    var now = Date.now();
    var items = raw.filter(function (x) {
      return x && typeof x.u === 'string' && x.u.indexOf('/products/') !== -1 && typeof x.ts === 'number' && now - x.ts < 2592000000;
    }).slice(0, 4);
    if (items.length < 2) return;

    var host = document.createElement('section');
    host.className = 'ancona-enhance ancona-recent ancona-explore-more';
    host.setAttribute('aria-label', 'Continue exploring');
    var inner = document.createElement('div');
    inner.className = 'ancona-container';
    var head = document.createElement('div');
    head.className = 'ancona-recent__head';
    var title = document.createElement('h2');
    title.className = 'ancona-recent__title';
    title.textContent = 'Continue exploring';
    var note = document.createElement('span');
    note.className = 'ancona-recent__note';
    note.textContent = 'Pieces viewed in this browser';
    var clearBtn = document.createElement('button');
    clearBtn.type = 'button'; clearBtn.className = 'ancona-recent__clear';
    clearBtn.textContent = 'Clear';
    clearBtn.setAttribute('aria-label', 'Clear recently viewed pieces');
    clearBtn.addEventListener('click', function () {
      try { LS.removeItem('ancona:recently-viewed'); } catch (e) {}
      host.remove(); announce('Recently viewed pieces cleared.');
    });
    head.appendChild(title); head.appendChild(note); head.appendChild(clearBtn);
    var grid = document.createElement('ul');
    grid.className = 'ancona-recent__grid';
    grid.setAttribute('role', 'list');
    items.forEach(function (it) {
      var li = document.createElement('li');
      li.className = 'ancona-recent__card';
      var a = document.createElement('a');
      a.href = it.u;
      a.setAttribute('data-ancona-cta', 'continue-exploring:' + it.u);
      if (it.i) {
        var img = document.createElement('img');
        img.src = it.i; img.alt = ''; img.loading = 'lazy'; img.decoding = 'async';
        img.width = 300; img.height = 300;
        img.onerror = function () { if (img.parentNode) img.parentNode.removeChild(img); };
        a.appendChild(img);
      }
      var t = document.createElement('span'); t.className = 'ancona-recent__t'; t.textContent = it.t || ''; a.appendChild(t);
      if (it.p) { var p = document.createElement('span'); p.className = 'ancona-recent__p'; p.textContent = it.p; a.appendChild(p); }
      li.appendChild(a); grid.appendChild(li);
    });
    inner.appendChild(head); inner.appendChild(grid); host.appendChild(inner);
    var newsletter = document.querySelector('.newsletter');
    var anchor = newsletter && newsletter.closest ? newsletter.closest('.shopify-section') : null;
    if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(host, anchor);
  }

  /* ---- 5) GUIDE TABLE OF CONTENTS (page template, >=3 h2s) ----------------- */
  function initToc() {
    if (!document.querySelector('.main-page-title')) return;
    var rte = document.querySelector('.shopify-section .rte');
    if (!rte || rte.querySelector('.ancona-toc')) return;
    var h2s = Array.prototype.slice.call(rte.querySelectorAll('h2'));
    if (h2s.length < 3) return;

    var used = {};
    h2s.forEach(function (h) {
      if (h.id) { used[h.id] = true; return; }
      var slug = (h.textContent || '').toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').slice(0, 60) || 'section';
      var id = slug, n = 2;
      while (used[id] || document.getElementById(id)) { id = slug + '-' + n; n++; }
      used[id] = true;
      h.id = id;
    });

    var nav = document.createElement('nav');
    nav.className = 'ancona-toc';
    nav.setAttribute('aria-label', 'On this page');
    var label = document.createElement('p');
    label.className = 'ancona-toc__label';
    label.textContent = 'On this page';
    var ul = document.createElement('ul');
    ul.setAttribute('role', 'list');
    h2s.forEach(function (h) {
      var li = document.createElement('li');
      var a = document.createElement('a');
      a.href = '#' + h.id;
      a.textContent = h.textContent || '';
      a.addEventListener('click', function () { track('guide_toc_click', { target: h.id }); });
      li.appendChild(a); ul.appendChild(li);
    });
    nav.appendChild(label); nav.appendChild(ul);
    h2s[0].parentNode.insertBefore(nav, h2s[0]);

    function markCurrent() {
      var id = location.hash.replace('#', '');
      Array.prototype.forEach.call(ul.querySelectorAll('a'), function (a) {
        if (a.getAttribute('href') === '#' + id) a.setAttribute('aria-current', 'true');
        else a.removeAttribute('aria-current');
      });
    }
    window.addEventListener('hashchange', markCurrent);
    markCurrent();
  }

  function boot() {
    /* Each feature is isolated: a throw in one can never abort the others. */
    var inits = [['saved', initSaved], ['shortlist', initShortlistView], ['return', initReturn], ['explore', initExplore], ['toc', initToc]];
    inits.forEach(function (pair) {
      if (!on(pair[0])) return;
      try { pair[1](); } catch (e) { /* one feature failing must not break the page */ }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
